import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "./api";

// GET `path` with `query`, refetching whenever either changes (the previous request is
// aborted). `data` keeps the last good response while a new one loads, so tables don't
// flash empty between pages. `status` is loading | ready | error for the latest request.
export default function useApiQuery(path, query) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const queryKey = JSON.stringify(query ?? {});
  const controllerRef = useRef(null);

  const load = useCallback(() => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setStatus("loading");
    apiRequest(path, { query: JSON.parse(queryKey), signal: controller.signal })
      .then((body) => {
        setData(body);
        setStatus("ready");
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error(`Failed to load ${path}:`, err);
        setStatus("error");
      });
  }, [path, queryKey]);

  useEffect(() => {
    load();
    return () => controllerRef.current?.abort();
  }, [load]);

  return { data, setData, status, reload: load };
}

// Debounced copy of `value`, for search boxes that query the API
export function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}
